import { useEffect, useState, useCallback } from "react";
import { Users, DollarSign, Activity, Star, Inbox, Clock, Loader2, AlertCircle, Dumbbell, Wifi, UserPlus, Video, CalendarClock, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";

const Card = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-[#0A0A0A] p-6" data-testid={`overview-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <Icon size={20} className="text-white/70 mb-3" />
    <p className="font-mono-accent text-4xl font-bold">{value}</p>
    <p className="text-white/40 text-xs uppercase tracking-wide mt-1">{label}</p>
    {sub && <p className="text-white/30 text-[11px] mt-1">{sub}</p>}
  </div>
);

export default function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState({ total: 0, newCount: 0 });
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: inq }, { data: m }] = await Promise.all([
        api.get("/memberships/stats"),
        api.get("/admin/inquiries"),
        api.get("/admin/metrics"),
      ]);
      setStats(s);
      setMetrics(m);
      setLeads({ total: inq.length, newCount: inq.filter((i) => i.status === "new").length });
    } catch (e) {
      setError("Failed to load dashboard stats. Please refresh.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error && !stats) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-white/50 gap-3" data-testid="overview-error">
        <AlertCircle size={28} className="text-red-400" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-24 text-white/40">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="overview-tab">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-8">
        Overview
      </h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        <Card icon={Users} label="Total Members" value={stats.total_members} />
        <Card icon={DollarSign} label="Est. MRR" value={`$${stats.mrr.toLocaleString()}`} sub="Active memberships / month" />
        <Card icon={Activity} label="Active" value={stats.by_status.active} />
        <Card
          icon={Star}
          label="Founding Spots"
          value={`${stats.founding_remaining}/${stats.founding_limit}`}
          sub={`${stats.founding_used} claimed`}
        />
        <Card icon={Inbox} label="Total Leads" value={leads.total} />
        <Card icon={Clock} label="New Leads" value={leads.newCount} />
      </div>

      {metrics && (
        <>
          <h2 className="font-display font-bold uppercase tracking-tight text-xl mt-12 mb-4">Athletes & Operations</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="overview-metrics">
            <Card icon={Users} label="Total Athletes" value={metrics.total_athletes} />
            <Card icon={Dumbbell} label="In-Person" value={metrics.in_person_athletes} />
            <Card icon={Wifi} label="Digital" value={metrics.digital_athletes} />
            <Card icon={UserPlus} label="New Signups (7d)" value={metrics.new_signups} />
            <Card icon={ClipboardCheck} label="Pending Evaluations" value={metrics.pending_evaluations} />
            <Card icon={Video} label="Pending Video Reviews" value={metrics.pending_video_reviews} />
            <Card icon={CalendarClock} label="Upcoming Retests" value={metrics.upcoming_retests} />
            <Card icon={DollarSign} label="Digital MRR" value={`$${(metrics.revenue_by_type?.digital || 0).toLocaleString()}`} />
          </div>
          {Object.keys(metrics.revenue_by_type || {}).length > 0 && (
            <div className="mt-6 flex flex-wrap gap-3" data-testid="overview-revenue-by-type">
              {Object.entries(metrics.revenue_by_type).map(([t, v]) => (
                <div key={t} className="border border-white/10 bg-[#121212] px-4 py-3">
                  <p className="font-mono-accent text-lg font-bold">${v.toLocaleString()}</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-wide">{t.replace("_", " ")} / mo</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
